import { apiRequest, apiUrl, queryString } from "./http";
import type { ApiError, ApiResponse } from "./http";
import type {
  AddParticipantPayload,
  ChatAttachment,
  ChatConversation,
  ChatConversationDetail,
  ChatMediaPage,
  ChatMediaParams,
  ChatMessage,
  ChatMessagePage,
  ChatParticipant,
  ConversationListResponse,
  CreateConversationPayload,
  MarkReadPayload,
  MarkReadResponse,
  MessageHistoryParams,
  ChatPreferences,
  ChatSearchPage,
  ChatSearchParams,
  RenameConversationPayload,
  SendMessagePayload,
  UnreadCountResponse,
} from "./generated/openapi";
export type {
  ChatConversation,
  ChatConversationDetail,
  ChatMediaPage,
  ChatMediaParams,
  ChatMessage,
  ChatMessagePage,
  ChatParticipant,
  ChatSocketEvent,
  ConversationType,
  CreateConversationPayload,
  ChatDigestFrequency,
  ChatPreferences,
  ChatSearchPage,
  ChatSearchParams,
  MessageHistoryParams,
  SendMessagePayload,
} from "./generated/openapi";
// Friendlier names for the generated attachment schema, which the composer
// and the media sidebar use rather than the `Chat`-prefixed generated ones.
export type { ChatAttachment as Attachment, ChatAttachmentKind as AttachmentKind } from "./generated/openapi";

export const chatApi = {
  listConversations: (signal?: AbortSignal) =>
    apiRequest<ConversationListResponse>(
      "/api/chat/conversations",
      {
        signal,
      },
    ),

  /**
   * The conversation attached to one project, resolved by the API.
   *
   * Deliberately not a scan of `listConversations`: that list is capped, so
   * filtering it client-side loses the project conversation for anyone with
   * more conversations than the cap.
   */
  getProjectConversation: (projectId: string, signal?: AbortSignal) =>
    apiRequest<ChatConversationDetail>(
      `/api/projects/${projectId}/conversation`,
      { signal },
    ),

  getConversation: (id: string, signal?: AbortSignal) =>
    apiRequest<ChatConversationDetail>(`/api/chat/conversations/${id}`, {
      signal,
    }),

  create: (payload: CreateConversationPayload) =>
    apiRequest<ChatConversationDetail>("/api/chat/conversations", {
      method: "POST",
      body: JSON.stringify(payload),
    }),

  rename: (id: string, title: string) =>
    apiRequest<ChatConversationDetail>(`/api/chat/conversations/${id}`, {
      method: "PATCH",
      body: JSON.stringify({ title } satisfies RenameConversationPayload),
    }),

  addParticipant: (id: string, userId: string) =>
    apiRequest<ChatConversationDetail>(
      `/api/chat/conversations/${id}/participants`,
      { method: "POST", body: JSON.stringify({ user_id: userId } satisfies AddParticipantPayload) },
    ),

  removeParticipant: (id: string, userId: string) =>
    apiRequest<ChatConversationDetail>(
      `/api/chat/conversations/${id}/participants/${userId}`,
      { method: "DELETE" },
    ),

  listMessages: (
    id: string,
    params: MessageHistoryParams = {},
    signal?: AbortSignal,
  ) =>
    apiRequest<ChatMessagePage>(
      `/api/chat/conversations/${id}/messages${queryString(params)}`,
      { signal },
    ),

  send: (id: string, body: string) =>
    apiRequest<ChatMessage>(`/api/chat/conversations/${id}/messages`, {
      method: "POST",
      body: JSON.stringify({ body } satisfies SendMessagePayload),
    }),

  /**
   * Sends a message that may carry attachment ids alongside (or instead of) a
   * body. Kept distinct from `send` rather than overloading it, so a plain
   * text send stays a one-argument call.
   */
  sendWithAttachments: (id: string, payload: SendMessagePayload) =>
    apiRequest<ChatMessage>(`/api/chat/conversations/${id}/messages`, {
      method: "POST",
      body: JSON.stringify(payload),
    }),

  /**
   * Lists a conversation's attachments for the media sidebar, newest first.
   */
  preferences: (signal?: AbortSignal) =>
    apiRequest<ChatPreferences>("/api/chat/preferences", { signal }),

  updatePreferences: (preferences: ChatPreferences) =>
    apiRequest<ChatPreferences>("/api/chat/preferences", {
      method: "PUT",
      body: JSON.stringify(preferences),
    }),

  /** Searches the caller's own history. Needs `q` or `has`. */
  search: (params: ChatSearchParams, signal?: AbortSignal) =>
    apiRequest<ChatSearchPage>(`/api/chat/search${queryString(params)}`, {
      signal,
    }),

  listMedia: (
    id: string,
    params: ChatMediaParams = {},
    signal?: AbortSignal,
  ) =>
    apiRequest<ChatMediaPage>(
      `/api/chat/conversations/${id}/media${queryString(params)}`,
      { signal },
    ),

  deleteMessage: (messageId: string) =>
    apiRequest<ChatMessage>(`/api/chat/messages/${messageId}`, {
      method: "DELETE",
    }),

  markRead: (id: string, readAt?: string) =>
    apiRequest<MarkReadResponse>(`/api/chat/conversations/${id}/read`, {
      method: "POST",
      body: JSON.stringify((readAt ? { read_at: readAt } : {}) satisfies MarkReadPayload),
    }),

  unreadCount: (signal?: AbortSignal) =>
    apiRequest<UnreadCountResponse>("/api/chat/unread-count", { signal }),
};

export interface UploadAttachmentOptions {
  /** A JPEG still captured from a video at upload time. Video only. */
  poster?: Blob;
  /** Fraction of the file body sent so far, in `[0, 1]`. */
  onProgress?: (fraction: number) => void;
  signal?: AbortSignal;
}

/**
 * Uploads one chat attachment.
 *
 * `apiRequest` is fetch-based, and fetch has no upload progress event, so this
 * goes around it with an `XMLHttpRequest` instead, matching `apiRequest`'s
 * `{ data, error }` shape and its error-body parsing so a 400 still surfaces
 * the API's message.
 */
export function uploadAttachment(
  conversationId: string,
  file: File,
  options: UploadAttachmentOptions = {},
): Promise<ApiResponse<ChatAttachment>> {
  const { poster, onProgress, signal } = options;

  return new Promise((resolve) => {
    const xhr = new XMLHttpRequest();
    const formData = new FormData();
    formData.append("file", file);
    if (poster) {
      formData.append("poster", poster);
    }

    const onAbort = () => xhr.abort();
    const settle = (result: ApiResponse<ChatAttachment>) => {
      signal?.removeEventListener("abort", onAbort);
      resolve(result);
    };

    xhr.upload.onprogress = (event) => {
      if (onProgress && event.lengthComputable) {
        onProgress(event.loaded / event.total);
      }
    };

    xhr.onload = () => {
      let data: unknown = null;
      try {
        data = xhr.responseText ? JSON.parse(xhr.responseText) : null;
      } catch {
        data = null;
      }
      if (xhr.status >= 200 && xhr.status < 300) {
        settle({ data: data as ChatAttachment, error: null });
        return;
      }
      const responseError = (data as { error?: Partial<ApiError> } | null)
        ?.error;
      settle({
        data: null,
        error: {
          code: responseError?.code,
          message: responseError?.message || xhr.statusText || "Upload failed",
          status: xhr.status,
        },
      });
    };

    xhr.onerror = () => {
      settle({ data: null, error: { message: "Network error" } });
    };

    xhr.onabort = () => {
      settle({ data: null, error: { message: "Upload cancelled" } });
    };

    signal?.addEventListener("abort", onAbort);

    xhr.open(
      "POST",
      apiUrl(`/api/chat/conversations/${conversationId}/attachments`),
    );
    xhr.withCredentials = true;
    xhr.send(formData);
  });
}

/** Absolute URL that downloads an attachment's original bytes. */
export function attachmentUrl(id: string): string {
  return apiUrl(`/api/chat/attachments/${id}`);
}

/** Absolute URL for an attachment's bounded JPEG preview. */
export function attachmentThumbnailUrl(id: string): string {
  return apiUrl(`/api/chat/attachments/${id}/thumbnail`);
}

/**
 * Absolute URL for the delivery socket.
 *
 * The session travels as the existing HttpOnly cookie on the upgrade, which
 * works because production is same-origin and because SameSite is evaluated
 * per site rather than per origin, so the dev split across ports 3001 and 4000
 * still sends it. No credential is ever placed in the query string.
 */
export function chatSocketUrl(): string {
  const url = new URL(apiUrl("/api/chat/ws"), window.location.origin);
  url.protocol = url.protocol === "https:" ? "wss:" : "ws:";
  return url.toString();
}

/**
 * Close code the API uses when the session behind an open socket is revoked or
 * expires. It is distinct from a transport failure so the client redirects to
 * sign-in instead of reconnecting forever.
 */
export const CHAT_CLOSE_SESSION_EXPIRED = 4401;

export function participantName(participant: ChatParticipant): string {
  const name = [participant.firstname, participant.lastname]
    .filter(Boolean)
    .join(" ")
    .trim();
  return name || "Portal user";
}

/**
 * Label for a conversation in a list.
 *
 * A direct message has no stored title, so it is named after the other person.
 * Falling back to a neutral label keeps the list readable when the only other
 * participant has since been archived.
 */
export function conversationTitle(
  conversation: Pick<ChatConversation, "type" | "title">,
  participants: ChatParticipant[],
  currentUserId: string | null | undefined,
): string {
  if (conversation.type === "group") {
    return conversation.title || "Group conversation";
  }
  const other = participants.find(
    (participant) => participant.user_id !== currentUserId,
  );
  return other ? participantName(other) : "Direct message";
}
